import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getObjectHash, gPointToTPosition, reKeyItem, roundDecimals } from '@iotv/datamodel';
import { getZombieInstanceFromPrimaryKey, isNonNaNNumber, isPosition } from '@iotv/iotv-v3-types';
import { Slider, Stack, Typography } from '@mui/material';
import { bearing } from '@turf/bearing';
import { distance } from '@turf/distance';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import { NodeEdgeMapperContext } from '../../ContextProviders/NodeEdgeMapperComponentContext';
import { createGetRangeColorFn, generateColorRange, rangify, simpleSliceRange } from '../../cosmetics/DataTheming/metricRanges';
import { getNiceDate } from '../../data/TypeHelpers';
import { GoogleMapAPIMapTypeIds } from '../../GoogleMapV4';
import { geoObjectCodec } from '../../GoogleMapV4/mapfns/ObjectConversion/validGeoObjectShims';
import { getBoundsFromNodeEdgeMapper } from '../../GoogleMapV4/mapfns/translation';
import { useThemedMap } from '../../GoogleMapV4/useThemedMap';
import { TransactionFunctions } from '../../redux/actions/AppActionFunctions/AppActions';
import store from '../../redux/stores/MainStore';
/** temp before setting actual max min for speed etc */
const rangeColors = generateColorRange(rangify(simpleSliceRange(0, 100, 20)), '#ff0000', '0000ff', 'white');
const debug = process.env.REACT_APP_DEBUG && false;
export const MapV4ShimView = ({ contextObject, viewObjects, polylines, position }) => {
    const [slice, setSlice] = useState([0, 0]);
    const [marks, setMarks] = useState([]);
    const [maxIdx, setMaxIdx] = useState(0);
    const [polylinesMap, setPolylinesMap] = useState({});
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const { state: { nodeEdgeMapper, nemHash }, dispatch, } = useContext(NodeEdgeMapperContext);
    const derivedBounds = getBoundsFromNodeEdgeMapper({ nodeEdgeMapper });
    const derivedPosition = position ?? contextObject?.location ?? derivedBounds?.getCenter();
    debug && console.log(`MapV4Shim got position`, { positionIn: position, derivedPosition });
    const getRangeColor = useRef(createGetRangeColorFn({ speedMs: rangeColors }));
    const rangeColorMapsRef = useRef({ default: { Path: { speedMs: rangeColors } } });
    const pathStyleFn = useCallback((feature) => {
        let strokeOpacity = 1;
        let strokeColor = 'yellow';
        const data = feature?.getProperty('data');
        if (isNonNaNNumber(data?.ageProxy)) {
            strokeOpacity = 0.4 + 0.6 * data.ageProxy;
        }
        if (isNonNaNNumber(data?.speedMs)) {
            strokeColor = getRangeColor.current('speedMs', data.speedMs * 1000).fillColor;
        }
        return { strokeColor, strokeWeight: 4, strokeOpacity };
    }, []);
    const { components: { MapDatalayerVisibility, MapLegend, PortalDiv }, map, state: { zoom, center, bounds, areas: mapAreas, places: mapPlaces, paths: mapPaths }, } = useThemedMap({
        canaryString: 'ObjectView MapV4Shim',
        contextObject,
        dataFn: (async (gob) => {
            return { ...gob, data: {} };
        }),
        functions: {},
        nodeEdgeMapper,
        areaData: [],
        lineDefiningPairs: [],
        nodeEdgeMapperDispatch: dispatch,
        options: {
            tilt: 0,
            mapTypeId: GoogleMapAPIMapTypeIds.terrain,
            center: isPosition(contextObject?.location) ? contextObject.location : null,
            disableDefaultUI: isSmallView,
            mapTypeControlOptions: {
                style: isSmallView ? google.maps.MapTypeControlStyle.DROPDOWN_MENU : google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                position: google.maps.ControlPosition.BOTTOM_LEFT,
            },
            fullscreenControlOptions: {
                position: google.maps.ControlPosition.BOTTOM_RIGHT,
            },
        },
        transactionFunctions: bindActionCreators(TransactionFunctions, store.dispatch),
        showDrawingTools: false,
        themesAndLabels: {
            rangeColorMaps: rangeColorMapsRef.current,
            Path: {
                styleFn: pathStyleFn,
                // LabelComponent: DefaultPortalLabel
            },
            pathDataAttributeKey: 'speedMs',
        },
    });
    const polyLinesHash = getObjectHash(polylines);
    const isSlice = (slice) => slice instanceof Array && slice.every(isNonNaNNumber);
    useEffect(() => {
        const polylinesMap = polylines;
        setPolylinesMap(polylinesMap ?? {});
        const polylineKeys = polylinesMap && Object.keys(polylinesMap);
        const polylineMapFirstKey = polylineKeys && polylineKeys.length > 0 ? polylineKeys[0] : null;
        const polylinePoints = polylineMapFirstKey && polylinesMap ? polylinesMap[polylineMapFirstKey] : [];
        const maxIdx = polylinePoints.length - 1;
        const initialIdxRange = [Math.max(0, maxIdx - Math.floor(polylinePoints.length / 10)), maxIdx];
        if (isSlice(slice)) {
            if (!(slice[0] === initialIdxRange[0] && slice[1] === initialIdxRange[1])) {
                const sliceLength = slice[1] - slice[0] + 1;
                const diff = 1;
                debug &&
                    console.log('Need to update slice ends', {
                        polyLength: polylinePoints.length,
                        incoming: polylinePoints,
                        slice,
                        sliceLength,
                        diff,
                        initialIdxRange,
                    });
                setSlice(initialIdxRange);
                debug && console.log(`MapView - setSlice`, initialIdxRange);
            }
        }
        else {
            console.log(`MapView - Warn slice invalid`, slice);
        }
        setMaxIdx(maxIdx);
        const maxLabels = 10;
        const radix = Math.max(4, Math.round(polylinePoints.length / maxLabels));
        const marks = polylinesMap && polylineMapFirstKey
            ? polylinesMap[polylineMapFirstKey]
                .map((geofencePoint, i) => {
                return {
                    value: i,
                    label: (_jsx(Typography, { sx: { paddingLeft: 4, transform: 'rotate(90deg)', fontSize: 10 }, children: geofencePoint.timestamp ? getNiceDate(geofencePoint.timestamp, { weekday: 'short' }) : i.toString() })),
                };
            })
                .filter((_geofencePoint, i) => i % radix === 0)
            : [];
        setMarks(marks);
    }, [polyLinesHash]);
    const filterPolyLineMap = () => {
        debug &&
            console.log('Polylines', {
                polylinesMap,
                maxIdx,
                slice,
            });
        const filteredPolylines = {};
        const [startIdx, endIdx] = slice;
        polylinesMap &&
            Object.entries(polylinesMap).forEach(([key, polyline]) => {
                filteredPolylines[key] = polyline.filter((point, i) => i >= startIdx && i <= endIdx);
            });
        debug && console.log('filtered Polylines', { filteredPolylines });
        return filteredPolylines;
    };
    /** each objects polyline as a single line - ideally a hasTimestaps state would toggle between approaches - DO NOT REMOVE */
    // useEffect(() => {
    //   debug && console.log('Polylines', {
    //      polylinesMap,  maxIdx, slice
    //   })
    //   const filteredPolylines: MapLike<GeofencePointsType[]> = filterPolyLineMap()
    //   const singlePathLikeObjects =  Object.entries( filteredPolylines ).map( ( [k, polylinePoints] ) => {
    //     const z = getZombieInstanceFromPrimaryKey( `Path:${k}`, {  points: polylinePoints,  })
    //     return  geoObjectCodec.toGeoObject( z )
    //   }).filter( existsFilter ) as ValidGeoObject[]
    //   if ( singlePathLikeObjects.length > 0) {
    //     const action: LoadAction = { type: 'loadItems', value: { items: singlePathLikeObjects, canaryString: 'MapView useEffect slice' } }
    //     // dispatch( action )
    //   }
    // }, [ slice ])
    /** each object polyline as a collection of lines to be individually shown and styled */
    useEffect(() => {
        debug &&
            console.log('Polylines', {
                polylinesMap,
                maxIdx,
                slice,
            });
        const filteredPolylines = filterPolyLineMap();
        const multplePathLikeObjects = Object.entries(filteredPolylines).map(([k, polylinePoints]) => {
            const individualLines = polylinePoints.reduce((a, c, i, arr) => {
                const nextPoint = arr[i + 1];
                if (nextPoint) {
                    const points = [c, nextPoint];
                    /*  here be dragons with identity */
                    const [_type, id] = k.split(':');
                    const z = getZombieInstanceFromPrimaryKey(`Path(${_type}):${id}_${i}`, { points, timestamp: nextPoint.timestamp });
                    if (c.timestamp && nextPoint.timestamp) {
                        const turfPt1 = gPointToTPosition(c);
                        const turfPt2 = gPointToTPosition(nextPoint);
                        const distanceM = distance(turfPt1, turfPt2, { units: 'meters' });
                        const heading = bearing(turfPt1, turfPt2, {});
                        const speedMs = roundDecimals(distanceM / ((nextPoint.timestamp - c.timestamp) / 1000), 2);
                        const ageProxy = roundDecimals(i / arr.length, 2);
                        z.data = { speedMs, heading, ageProxy };
                    }
                    const subPath = geoObjectCodec.toGeoObject(z);
                    if (subPath) {
                        a.push(subPath);
                    }
                }
                return a;
            }, []);
            return individualLines;
        });
        if (multplePathLikeObjects.length > 0) {
            /* TODO decide whether map or nodeEdgeMapper get pseudo lines based on lat lng history.
            With mapDisptach
            */
            // const action: LoadAction = { type: 'loadItems', value: { items: multplePathLikeObjects.flat(), canaryString: 'MapView useEffect slice' } }
            // dispatch( action )
            // const mapAction: UndoReducerUpdateAction<PartialMapStateUpdate> = { type: 'update', value:{ subType: 'stateUpdate', stateUpdate: { paths: [ ...multplePathLikeObjects.flat().values() ] }} }
            debug && console.log('WARN: 2024 09 19 suppressed MapView disptaching polylines as individual paths to map - these must go through NodeEdgeMapper');
            const action = { type: 'loadItems', value: { items: multplePathLikeObjects.flat(), canaryString: 'MapView useEffect slice' } };
            dispatch(action);
        }
    }, [slice]);
    const derivedPositionHash = getObjectHash(derivedPosition);
    useEffect(() => {
        if (contextObject) {
            if (isPosition(derivedPosition)) {
                // this needs revisiting as it should be a clone
                contextObject.location = derivedPosition;
                if (!contextObject.points) {
                    contextObject.points = [derivedPosition];
                }
                debug && console.log(`WARN: set position as `, contextObject.location);
            }
            else {
                debug && console.log('MapView useEffect not position ', derivedPosition);
            }
            const shimmedObject = geoObjectCodec.toGeoObject(contextObject);
            if (shimmedObject) {
                if (shimmedObject.type) {
                    const addPlaceUpdate = {
                        type: 'loadItems',
                        value: { items: [shimmedObject], canaryString: 'MapViewV2 shim useEffect' },
                    };
                    debug && console.log('MapView dispatching ', { update: addPlaceUpdate });
                    dispatch(addPlaceUpdate);
                }
            }
            else {
                debug && console.log('MapView not dispatching context object as place', { contextObject });
                /** this attempt to shim instead of add position data gets lost after dispatch */
                if (derivedPosition) {
                    const shimmedObject = geoObjectCodec.toGeoObject({ ...contextObject, location: derivedPosition });
                    if (shimmedObject) {
                        const pseudoPlace = reKeyItem(shimmedObject, shimmedObject.id, shimmedObject.type); /** currently the same type and id, eg no-op */
                        const addPlaceUpdate = {
                            type: 'loadItems',
                            value: { items: [pseudoPlace], canaryString: 'MapView dispacth pseudoGeoObject' },
                        };
                        debug && console.log('MapView dispatching pseudo context object', { update: addPlaceUpdate });
                        dispatch(addPlaceUpdate);
                    }
                }
            }
        }
    }, [viewObjects?.length, contextObject?.sk, derivedPositionHash]);
    const surmz = (p) => p.id.substring(0, 4);
    const sumzObs = (ps) => ps.map(surmz).join(', ');
    debug && slice instanceof Array && console.log(`start: ${slice[0]} end: ${slice[1]} sliceLength: ${slice[1] - slice[0]} `);
    const direction = isSmallView ? 'column' : 'row';
    const controlsMargin = 12;
    const controlsWidth = isSmallView ? '250px' : `calc(100% - ${2 * controlsMargin}px)`;
    const justifyContent = isSmallView ? undefined : 'flex-end';
    const spacing = isSmallView ? 0 : 1;
    const controlsStack = (_jsxs(Stack, { width: controlsWidth, spacing, direction, sx: { zIndex: 2, height: 0, position: 'relative', top: controlsMargin, left: controlsMargin }, children: [_jsx(Stack, { direction, spacing, width: isSmallView ? undefined : '75%', children: _jsx(Stack, { children: MapDatalayerVisibility }, 'datalayerVisibility') }), _jsx(Stack, { direction, width: isSmallView ? '100%' : '25%', justifyContent, children: _jsx(Stack, { children: MapLegend }, 'legend') })] }, 'controls'));
    return (_jsxs(Stack, { direction: 'column', sx: { width: 'min(80vw, calc(100cqw - 2em))' }, children: [process.env.REACT_APP_IS_SUDO && (_jsxs(_Fragment, { children: [_jsx(Stack, { direction: 'column', sx: { width: '100%' }, children: `Map Data X: ${JSON.stringify({ center, zoom, bounds })}` }, 'data'), _jsx(Stack, { children: `Map has ${mapPlaces.length} places ${sumzObs(mapPlaces)}` }, 'placesLength'), _jsx(Stack, { children: `Map has ${mapAreas.length} areas  ${sumzObs(mapAreas)}` }, 'areasLength'), _jsx(Stack, { children: `Map has ${mapPaths.length} paths  nem has ${nodeEdgeMapper.getNodesOfType('Path').length} paths` }, 'pathsLengths')] })), _jsx(Stack, { sx: { position: 'relative', maxHeight: '2em', zIndex: 2 }, children: controlsStack }, 'controls'), _jsxs(Stack, { id: 'mapViewV4Stack', sx: { height: '60vh', width: '100%' }, children: [map.current, polylines && slice && marks && maxIdx > 0 && (_jsx(Slider, { max: maxIdx,
                        min: 0,
                        value: slice,
                        onChange: (_event, newValue) => isSlice(newValue) && setSlice(newValue),
                        marks,
                        valueLabelDisplay: 'off' }))] }, 'map'), _jsx(Stack, { children: PortalDiv }, 'portal')] }, 'MapViewV4'));
};
